import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  MessageSquare,
  Ticket,
  Users,
  MessageCircle,
  BookOpen,
  GitBranch,
  Store,
  BarChart2,
  Award,
  Star,
  ChevronLeft,
  ChevronRight,
  Settings,
  Lock,
  Shield,
  Users2,
  Radio,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';
import { cn } from '../../lib/utils';
import { useFeature, usePlan, type Plan } from '../../hooks/useFeature';
import ServeGraceWordmark from '../wordmark/ServeGraceWordmark';
import StatusBar from './StatusBar';
import Tooltip from '../ui/Tooltip';

/**
 * Shows the Admin link when the API confirms this user has platform
 * superadmin access (the permanent superadmin, or any OrgMember row with
 * is_superadmin = true). Single source of truth: GET /api/admin/me.
 */
function useIsSuperAdmin(): boolean {
  const { isSignedIn } = useAuth();
  const { data } = useQuery({
    queryKey: ['admin', 'me'],
    queryFn: async () => (await api.get<{ is_superadmin: boolean }>('/api/admin/me')).data,
    enabled: isSignedIn === true,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  return data?.is_superadmin === true;
}

interface NavItem {
  href: string;
  label: string;
  icon: LucideIcon;
  feature?: string;
}

const navItems: NavItem[] = [
  { href: '/conversations', label: 'Conversations', icon: MessageSquare },
  { href: '/tickets', label: 'Tickets', icon: Ticket },
  { href: '/customers', label: 'Customers', icon: Users },
  { href: '/leads', label: 'Leads', icon: UserPlus, feature: 'lead_capture' },
  { href: '/team-chat', label: 'Team Chat', icon: MessageCircle, feature: 'team_chat' },
  { href: '/knowledge-base', label: 'Knowledge Base', icon: BookOpen },
  { href: '/marketplace', label: 'Marketplace', icon: Store },
  { href: '/flows', label: 'Flows', icon: GitBranch },
  { href: '/analytics', label: 'Analytics', icon: BarChart2 },
  { href: '/workload', label: 'Team Workload', icon: Users2 },
  { href: '/monitor', label: 'Live Monitor', icon: Radio },
  { href: '/coaching', label: 'Coaching', icon: Award, feature: 'agent_coaching' },
  { href: '/quality', label: 'Quality Review', icon: Star, feature: 'ai_quality_scoring' },
];

const planColors: Record<Plan, string> = {
  free: 'bg-surface-3 text-text-3 border border-border',
  starter: 'bg-accent/10 text-accent border border-accent/20',
  growth: 'bg-accent-2/10 text-accent-2 border border-accent-2/20',
  scale: 'bg-blue-400/10 text-blue-400 border border-blue-400/20',
  enterprise: 'bg-accent-3/10 text-accent-3 border border-accent-3/20',
};

const planLabels: Record<Plan, string> = {
  free: 'Free',
  starter: 'Starter',
  growth: 'Growth',
  scale: 'Scale',
  enterprise: 'Enterprise',
};

function AdminNavLink() {
  const location = useLocation();
  const isActive = location.pathname.startsWith('/admin');
  return (
    <Link
      to="/admin"
      className={cn(
        'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent border-l-2',
        isActive
          ? 'bg-accent/10 text-accent border-accent pl-2.5'
          : 'text-text-2 hover:bg-surface-2 hover:text-text border-transparent',
      )}
    >
      <Shield size={18} className="shrink-0" />
      <span>Admin</span>
    </Link>
  );
}

function AdminNavLinkCollapsed() {
  const location = useLocation();
  const isActive = location.pathname.startsWith('/admin');
  return (
    <Tooltip content="Admin" side="right">
      <Link
        to="/admin"
        className={cn(
          'flex items-center justify-center p-2 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
          isActive ? 'text-accent bg-accent/10' : 'text-text-3 hover:bg-surface-2 hover:text-text',
        )}
      >
        <Shield size={18} />
      </Link>
    </Tooltip>
  );
}

function NavLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const location = useLocation();
  const allowed = useFeature(item.feature);
  const isActive = location.pathname.startsWith(item.href);
  const Icon = item.icon;

  const inner = (
    <span
      className={cn(
        'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 w-full',
        isActive
          ? 'bg-accent/10 text-accent border-l-2 border-accent pl-2.5'
          : allowed
          ? 'text-text-2 hover:bg-surface-2 hover:text-text border-l-2 border-transparent'
          : 'text-text-3 border-l-2 border-transparent cursor-not-allowed opacity-60',
        collapsed && 'justify-center px-0 pl-0 border-l-0',
      )}
    >
      <Icon size={18} className="shrink-0" />
      {!collapsed && <span className="truncate">{item.label}</span>}
      {!collapsed && !allowed && <Lock size={12} className="ml-auto shrink-0 text-text-3" />}
    </span>
  );

  const wrapped = collapsed ? (
    <Tooltip content={item.label} side="right">
      <span className="block">{inner}</span>
    </Tooltip>
  ) : (
    inner
  );

  if (!allowed) {
    return <div className="select-none">{wrapped}</div>;
  }

  return (
    <Link to={item.href} className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-lg">
      {wrapped}
    </Link>
  );
}

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const plan = usePlan();
  const isAdmin = useIsSuperAdmin();

  return (
    <aside
      className={cn(
        'flex flex-col h-screen bg-surface border-r border-border transition-all duration-300 shrink-0',
        collapsed ? 'w-[60px]' : 'w-[220px]',
      )}
    >
      {/* Header */}
      <div
        className={cn(
          'flex items-center justify-between px-4 py-4 border-b border-border',
          collapsed && 'justify-center px-2',
        )}
      >
        {!collapsed && (
          <Link to="/dashboard" className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded">
            <ServeGraceWordmark />
          </Link>
        )}
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="p-1 rounded-md text-text-3 hover:text-text hover:bg-surface-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      {/* Plan badge */}
      {!collapsed && (
        <div className="px-4 pt-3 pb-1">
          <span
            className={cn(
              'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
              planColors[plan],
            )}
          >
            {planLabels[plan]}
          </span>
        </div>
      )}

      {/* Nav items */}
      <nav className={cn('flex-1 overflow-y-auto py-2 px-2 flex flex-col gap-0.5')}>
        {navItems.map((item) => (
          <NavLink key={item.href} item={item} collapsed={collapsed} />
        ))}
      </nav>

      {/* Bottom */}
      <div className="border-t border-border px-2 py-2 flex flex-col gap-1">
        <StatusBar collapsed={collapsed} />

        {/* Admin link — superadmin only */}
        {isAdmin && (
          collapsed ? <AdminNavLinkCollapsed /> : <AdminNavLink />
        )}

        {collapsed ? (
          <Tooltip content="Settings" side="right">
            <Link
              to="/settings"
              className="flex items-center justify-center p-2 rounded-lg text-text-3 hover:bg-surface-2 hover:text-text transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <Settings size={18} />
            </Link>
          </Tooltip>
        ) : (
          <Link
            to="/settings"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-text-2 hover:bg-surface-2 hover:text-text transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <Settings size={18} className="shrink-0" />
            <span>Settings</span>
          </Link>
        )}

        {!collapsed && (
          <p className="px-3 pt-1 text-[10px] text-text-3 text-center">© 2026 Pure Grace AI, LLC</p>
        )}
      </div>
    </aside>
  );
}
